/**
 * WorkflowChrome.tsx — Workflow 容器树显示轨（display + scoped loop）
 */
import { useMemo, useState, type ReactNode } from 'react';

import { useWorkflowScaleOptions } from '../../core/context/WorkflowScaleContext';
import type { SessionTree } from '../../types/tree';
import type {
  WorkflowContainerRuntimeNode,
  WorkflowContainerTreeState,
} from '../../types/workflowContainer';
import { WorkflowAggregateContainer } from './WorkflowAggregateContainer';
import { WorkflowContainerLine } from './WorkflowContainerLine';
import { WorkflowParallelGroup } from './WorkflowParallelGroup';
import { WorkflowRootContainer } from './WorkflowRootContainer';
import { WorkflowRouteContainer } from './WorkflowRouteContainer';
import { WorkflowStageContainer } from './WorkflowStageContainer';
import { WorkflowSubworkflowContainer } from './WorkflowSubworkflowContainer';
import { partitionWorkflowStages, subtreeHasActiveWork } from './workflowStageListUtils';

export interface WorkflowChromeProps {
  containerTree: WorkflowContainerTreeState;
  loopTreesBySessionId?: Record<string, SessionTree>;
  virtualized?: boolean;
  virtualizeThreshold?: number;
  groupParallelTools?: boolean;
}

type ViewProps = Pick<
  WorkflowChromeProps,
  'virtualized' | 'virtualizeThreshold' | 'groupParallelTools'
>;

function collectChildContainers(
  containersById: Record<string, WorkflowContainerRuntimeNode>,
  parentId: string,
): WorkflowContainerRuntimeNode[] {
  return Object.values(containersById)
    .filter((node) => node.parent_container_id === parentId)
    .sort((a, b) => a.scope_key.localeCompare(b.scope_key));
}

function resolveLoopTree(
  node: WorkflowContainerRuntimeNode,
  loopTreesBySessionId: Record<string, SessionTree>,
): SessionTree | undefined {
  if (!node.loopSessionId) return undefined;
  return loopTreesBySessionId[node.loopSessionId];
}

function collectSiblingItems(
  node: WorkflowContainerRuntimeNode,
  containersById: Record<string, WorkflowContainerRuntimeNode>,
): WorkflowContainerRuntimeNode[] {
  if (!node.parent_container_id) return [];
  return collectChildContainers(containersById, node.parent_container_id).filter(
    (child) => child.scope === 'item',
  );
}

function renderLeafContainer(
  node: WorkflowContainerRuntimeNode,
  depth: number,
  containerTree: WorkflowContainerTreeState,
  loopTreesBySessionId: Record<string, SessionTree>,
  viewProps: ViewProps,
  options?: { suppressHostScopedLoop?: boolean; subtreeHasActiveWork?: boolean },
): ReactNode {
  const loopTree = resolveLoopTree(node, loopTreesBySessionId);
  const subtreeActive =
    options?.subtreeHasActiveWork
    ?? subtreeHasActiveWork(containerTree.containersById, node.container_id);

  switch (node.scope) {
    case 'aggregate':
      return (
        <WorkflowAggregateContainer
          key={node.container_id}
          node={node}
          loopTree={loopTree}
          siblingItems={collectSiblingItems(node, containerTree.containersById)}
          depth={depth}
          {...viewProps}
        />
      );
    case 'branch':
      return (
        <WorkflowRouteContainer
          key={node.container_id}
          node={node}
          loopTree={loopTree}
          depth={depth}
          {...viewProps}
        />
      );
    default:
      return (
        <WorkflowStageContainer
          key={node.container_id}
          node={node}
          loopTree={loopTree}
          depth={depth}
          suppressHostScopedLoop={options?.suppressHostScopedLoop}
          subtreeHasActiveWork={subtreeActive}
          {...viewProps}
        />
      );
  }
}

function renderItemSiblings(
  items: WorkflowContainerRuntimeNode[],
  groupKey: string,
  depth: number,
  containerTree: WorkflowContainerTreeState,
  loopTreesBySessionId: Record<string, SessionTree>,
  viewProps: ViewProps,
): ReactNode {
  if (items.length >= 2) {
    return (
      <WorkflowParallelGroup
        groupKey={groupKey}
        items={items}
        loopTreesBySessionId={loopTreesBySessionId}
        depth={depth}
        {...viewProps}
      />
    );
  }
  if (items.length === 1) {
    return renderLeafContainer(items[0]!, depth, containerTree, loopTreesBySessionId, viewProps);
  }
  return null;
}

function stageHasSubworkflowChild(
  stageId: string,
  containerTree: WorkflowContainerTreeState,
): boolean {
  return collectChildContainers(containerTree.containersById, stageId).some(
    (node) => node.scope === 'subworkflow',
  );
}

function renderContainerChildren(
  parentId: string,
  containerTree: WorkflowContainerTreeState,
  loopTreesBySessionId: Record<string, SessionTree>,
  viewProps: ViewProps,
  depth: number,
): ReactNode {
  const children = collectChildContainers(containerTree.containersById, parentId);
  const items = children.filter((node) => node.scope === 'item');
  const rest = children.filter((node) => node.scope !== 'item');

  return (
    <>
      {renderItemSiblings(items, parentId, depth, containerTree, loopTreesBySessionId, viewProps)}
      {rest.map((node) => renderContainerNode(node, containerTree, loopTreesBySessionId, viewProps, depth))}
    </>
  );
}

function renderContainerNode(
  node: WorkflowContainerRuntimeNode,
  containerTree: WorkflowContainerTreeState,
  loopTreesBySessionId: Record<string, SessionTree>,
  viewProps: ViewProps,
  depth: number,
): ReactNode {
  const nested = renderContainerChildren(
    node.container_id,
    containerTree,
    loopTreesBySessionId,
    viewProps,
    depth + 1,
  );
  const hasNested =
    collectChildContainers(containerTree.containersById, node.container_id).length > 0;

  if (node.scope === 'subworkflow') {
    return (
      <WorkflowSubworkflowContainer
        key={node.container_id}
        node={node}
        depth={depth}
        subtreeHasActiveWork={subtreeHasActiveWork(
          containerTree.containersById,
          node.container_id,
        )}
      >
        {hasNested ? nested : null}
      </WorkflowSubworkflowContainer>
    );
  }

  if (node.scope === 'stage') {
    const suppressHostScopedLoop = stageHasSubworkflowChild(node.container_id, containerTree);
    return (
      <div
        key={node.container_id}
        className="lax-workflow-nested-block"
        data-testid={`lax-workflow-nested-${node.scope}-${node.scope_key}`}
      >
        {renderLeafContainer(
          node,
          depth,
          containerTree,
          loopTreesBySessionId,
          viewProps,
          { suppressHostScopedLoop },
        )}
        {hasNested ? (
          <div className="lax-workflow-container-children">{nested}</div>
        ) : null}
      </div>
    );
  }

  if (node.scope === 'aggregate' || node.scope === 'branch') {
    return (
      <div key={node.container_id} className="lax-workflow-container-children">
        {renderLeafContainer(node, depth, containerTree, loopTreesBySessionId, viewProps)}
        {hasNested ? nested : null}
      </div>
    );
  }

  return renderLeafContainer(node, depth, containerTree, loopTreesBySessionId, viewProps);
}

function renderWorkflowStageContent(
  node: WorkflowContainerRuntimeNode,
  containerTree: WorkflowContainerTreeState,
  loopTreesBySessionId: Record<string, SessionTree>,
  viewProps: ViewProps,
): ReactNode {
  return renderContainerNode(node, containerTree, loopTreesBySessionId, viewProps, 1);
}

function renderWorkflowStageRow(
  node: WorkflowContainerRuntimeNode,
  containerTree: WorkflowContainerTreeState,
  loopTreesBySessionId: Record<string, SessionTree>,
  viewProps: ViewProps,
  as: 'li' | 'div' = 'li',
): ReactNode {
  const className = 'lax-workflow-stage-list__item';
  const content = renderWorkflowStageContent(
    node,
    containerTree,
    loopTreesBySessionId,
    viewProps,
  );
  if (as === 'div') {
    return (
      <div key={node.container_id} className={className} role="listitem">
        {content}
      </div>
    );
  }
  return (
    <li key={node.container_id} className={className}>
      {content}
    </li>
  );
}

function WorkflowStageListPanel({
  stages,
  containerTree,
  loopTreesBySessionId,
  viewProps,
}: {
  stages: WorkflowContainerRuntimeNode[];
  containerTree: WorkflowContainerTreeState;
  loopTreesBySessionId: Record<string, SessionTree>;
  viewProps: ViewProps;
}) {
  const scale = useWorkflowScaleOptions();
  // P3：分页计数 — 每次点击“Show N more”递增一页，避免一次性展开 180 done stage
  const [extraCompletedPages, setExtraCompletedPages] = useState(0);
  const maxVisibleDoneCount =
    scale.completedStagePageSize + extraCompletedPages * scale.completedStagePageSize;

  const partition = useMemo(
    () => partitionWorkflowStages(stages, maxVisibleDoneCount, containerTree.containersById),
    [stages, maxVisibleDoneCount, containerTree.containersById],
  );

  if (partition.visible.length === 0) return null;

  const showMoreCount = Math.min(
    scale.completedStagePageSize,
    partition.totalDoneCount - partition.visibleDoneCount,
  );

  return (
    <>
      <ol className="lax-workflow-stage-list" data-testid="lax-workflow-stage-list">
        {partition.visible.map((node) =>
          renderWorkflowStageRow(node, containerTree, loopTreesBySessionId, viewProps),
        )}
      </ol>

      {partition.hiddenCount > 0 ? (
        <button
          type="button"
          className="lax-workflow-stage-list__show-all"
          data-testid="lax-workflow-stage-show-more"
          onClick={() => setExtraCompletedPages((p) => p + 1)}
        >
          +{partition.hiddenCount} completed stages hidden · Show {showMoreCount} more
        </button>
      ) : null}

      {extraCompletedPages > 0 && partition.hiddenCount === 0 ? (
        <button
          type="button"
          className="lax-workflow-stage-list__show-all"
          data-testid="lax-workflow-stage-show-less"
          onClick={() => setExtraCompletedPages(0)}
        >
          Show fewer completed stages
        </button>
      ) : null}

      {partition.hiddenCount > 0 ? (
        <p
          className="lax-workflow-stage-list__hidden-hint"
          data-testid="lax-workflow-stage-hidden-hint"
        >
          {partition.hiddenCount} completed stage{partition.hiddenCount !== 1 ? 's' : ''} hidden —
          running, pending, and failed stages are always visible.
        </p>
      ) : null}
    </>
  );
}

function renderWorkflowChildren(
  workflowNode: WorkflowContainerRuntimeNode,
  containerTree: WorkflowContainerTreeState,
  loopTreesBySessionId: Record<string, SessionTree>,
  viewProps: ViewProps,
) {
  const children = collectChildContainers(
    containerTree.containersById,
    workflowNode.container_id,
  );
  const stages = children.filter((node) => node.scope === 'stage');
  const items = children.filter((node) => node.scope === 'item');
  const other = children.filter(
    (node) => node.scope !== 'stage' && node.scope !== 'item',
  );

  return (
    <>
      {stages.length > 0 ? (
        <WorkflowStageListPanel
          stages={stages}
          containerTree={containerTree}
          loopTreesBySessionId={loopTreesBySessionId}
          viewProps={viewProps}
        />
      ) : null}

      {renderItemSiblings(items, workflowNode.container_id, 1, containerTree, loopTreesBySessionId, viewProps)}

      {other.map((node) =>
        renderContainerNode(node, containerTree, loopTreesBySessionId, viewProps, 1),
      )}
    </>
  );
}

export function WorkflowChrome({
  containerTree,
  loopTreesBySessionId = {},
  virtualized,
  virtualizeThreshold,
  groupParallelTools,
}: WorkflowChromeProps) {
  const viewProps = { virtualized, virtualizeThreshold, groupParallelTools };
  const rootNodes = containerTree.rootContainerIds
    .map((id) => containerTree.containersById[id])
    .filter((node): node is WorkflowContainerRuntimeNode => node != null);

  if (rootNodes.length === 0) {
    return (
      <div className="lax-workflow-chrome" data-testid="lax-workflow-chrome">
        <WorkflowContainerLine
          variant="root"
          title="Workflow"
          uiStatus="pending"
          statusLabel="pending"
        />
      </div>
    );
  }

  return (
    <div className="lax-workflow-chrome" data-testid="lax-workflow-chrome">
      {rootNodes.map((workflowNode) => (
        <WorkflowRootContainer
          key={workflowNode.container_id}
          node={workflowNode}
          containersById={containerTree.containersById}
        >
          {renderWorkflowChildren(
            workflowNode,
            containerTree,
            loopTreesBySessionId,
            viewProps,
          )}
        </WorkflowRootContainer>
      ))}
    </div>
  );
}
